import React, { useState, useEffect } from "react";
import {
  playClickSound,
  playCashRegisterSound,
} from "../../utils/soundUtils.js";
import { getLocation } from "../../data/locations.js";
import { getConversation } from "../../data/conversations.js";
import { FaArrowLeft } from "react-icons/fa";
import QuoteCarousel from "../QuoteCarousel.js";
import TextReveal from "../TextReveal.js";
import StoryComponent from "../StoryComponent.js";
import ConversationComponent from "../ConversationComponent.js";
import { stories } from "../../data/stories.js";
import { getFromStorage } from "../../utils/localStorage.js";

function LocationMenu({
  locationId,
  character,
  onNavigate,
  onShowInventory,
  onToggleMobileStats,
  showMobileStats,
  onUpdateCharacter,
}) {
  const location = getLocation(locationId);
  const [shouldTypeDescription, setShouldTypeDescription] = useState(false);
  const [showQuotes, setShowQuotes] = useState(false);
  const [showStory, setShowStory] = useState(false);
  const [showConversation, setShowConversation] = useState(false);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [showRentConfirmation, setShowRentConfirmation] = useState(false);

  // Check if this location has been visited before
  const hasVisitedLocation = () => {
    const visitedLocations = JSON.parse(
      localStorage.getItem("visitedLocations") || "[]"
    );
    return visitedLocations.includes(locationId);
  };

  // Mark location as visited
  const markLocationAsVisited = () => {
    const visitedLocations = JSON.parse(
      localStorage.getItem("visitedLocations") || "[]"
    );
    if (!visitedLocations.includes(locationId)) {
      visitedLocations.push(locationId);
      localStorage.setItem(
        "visitedLocations",
        JSON.stringify(visitedLocations)
      );
    }
  };

  // Check if we should show story or use typewriter effect
  useEffect(() => {
    if (!location) return;

    // Check if this location has a story and if it hasn't been completed
    const hasStory = stories[locationId];
    const completedStories = getFromStorage("completedStories", []);
    const storyCompleted = completedStories.includes(locationId);

    if (hasStory && !storyCompleted) {
      // Show story first
      setShowStory(true);
      setShouldTypeDescription(false);
      setShowQuotes(false);
    } else if (hasVisitedLocation()) {
      // If visited before, don't use typewriter effect and show quotes immediately
      setShouldTypeDescription(false);
      setShowQuotes(true);
      setShowStory(false);
    } else {
      // First visit - use typewriter effect and hide quotes initially
      setShouldTypeDescription(true);
      setShowQuotes(false);
      setShowStory(false);
    }
  }, [locationId, location]);

  // Mark location as visited and show quotes when typing completes
  const handleDescriptionComplete = () => {
    markLocationAsVisited();
    setShowQuotes(true);
  };

  // Handle story completion
  const handleStoryComplete = () => {
    setShowStory(false);
    // After story completes, show the normal location content
    if (hasVisitedLocation()) {
      setShowQuotes(true);
    } else {
      setShouldTypeDescription(true);
    }
  };

  // Handle starting a conversation
  const handleStartConversation = (personId) => {
    const conversation = getConversation(personId);
    if (conversation) {
      setCurrentConversation(conversation);
      setShowConversation(true);
    }
  };

  // Handle closing conversation
  const handleCloseConversation = () => {
    setShowConversation(false);
    setCurrentConversation(null);
  };

  // Handle hotel room rental
  const handleRentRoom = () => {
    playClickSound();
    setShowRentConfirmation(true);
  };

  const confirmRentRoom = () => {
    if (!location.hotel || !onUpdateCharacter) return;

    const { price, health } = location.hotel;

    // Deduct credits and restore health
    const updatedCharacter = {
      ...character,
      credits: character.credits - price,
      hp: Math.min(character.maxHp, character.hp + health),
    };

    onUpdateCharacter(updatedCharacter);
    setShowRentConfirmation(false);
    playCashRegisterSound();
  };

  const cancelRentRoom = () => {
    playClickSound();
    setShowRentConfirmation(false);
  };

  if (!location) {
    return (
      <div className="page-container">
        <div className="back-button-row">
          <button className="back-button" onClick={() => onNavigate("streets")}>
            <FaArrowLeft />
            Back
          </button>
        </div>
        <div className="location-screen">
          <div className="location-header">
            <h2>Unknown Location</h2>
          </div>
          <div className="location-description">
            This location could not be found.
          </div>
        </div>
      </div>
    );
  }

  // Show story component if story should be displayed
  if (showStory) {
    return (
      <StoryComponent
        locationId={locationId}
        onComplete={handleStoryComplete}
      />
    );
  }

  // Show conversation component if conversation should be displayed
  if (showConversation && currentConversation) {
    return (
      <ConversationComponent
        conversation={currentConversation}
        onClose={handleCloseConversation}
      />
    );
  }

  // Determine the back navigation based on location structure
  const getBackLocation = () => {
    if (location.parent) {
      return location.parent;
    }
    if (locationId === "chiba-city") {
      return "streets";
    }
    if (locationId === "ninsei-strip" || locationId === "corporate-arcology") {
      return "chiba-city";
    }
    return "streets"; // fallback
  };

  // Determine if this is a top-level menu (has Inventory and Help)
  const isTopLevel = locationId === "streets" || locationId === "chiba-city";

  return (
    <div className="page-container">
      {/* Back Button Row */}
      <div className="back-button-row">
        <button
          className="back-button"
          onClick={() => onNavigate(getBackLocation())}
        >
          <FaArrowLeft />
          Back
        </button>
      </div>

      <div className="location-screen">
        <div className="location-header">
          <h2>{location.name}</h2>
        </div>

        <div className="location-description">
          {shouldTypeDescription ? (
            <TextReveal
              text={location.description || ""}
              speed={30}
              onComplete={handleDescriptionComplete}
            />
          ) : (
            location.description
          )}
        </div>

        {showQuotes && (
          <QuoteCarousel
            locationId={locationId}
            quotes={location.quotes || []}
          />
        )}

        <div className="options-grid">
          {/* Render sub-locations if they exist */}
          {location.locations &&
            location.locations.map((subLocationId) => {
              const subLocation = getLocation(subLocationId);
              return (
                <div key={subLocationId} className="option-row">
                  <button
                    className="menu-button"
                    onClick={() => {
                      playClickSound();
                      onNavigate(subLocationId);
                    }}
                  >
                    {subLocation ? subLocation.name : subLocationId}
                  </button>
                </div>
              );
            })}

          {/* Render Talk to [person] buttons if people exist */}
          {location.people &&
            location.people.map((person) => (
              <div key={person.id} className="option-row">
                <button
                  className="menu-button"
                  onClick={() => {
                    playClickSound();
                    handleStartConversation(person.id);
                  }}
                >
                  Talk to {person.name}
                </button>
              </div>
            ))}

          {/* Render Shop button for market-type locations */}
          {location.type === "market" && (
            <div className="option-row">
              <button
                className="menu-button"
                onClick={() => {
                  playClickSound();
                  onNavigate("market", {
                    title: location.name,
                    locationFilter: locationId,
                  });
                }}
              >
                Shop
              </button>
            </div>
          )}

          {/* Render Combat button for combat-type locations */}
          {location.type === "combat" && (
            <div className="option-row">
              <button
                className="menu-button"
                onClick={() => {
                  playClickSound();
                  onNavigate("combat", {
                    locationName: location.name,
                    locationId: locationId,
                    enemyIds: location.enemyIds || [],
                  });
                }}
              >
                Combat
              </button>
            </div>
          )}

          {/* Render Rent button for hotel-type locations */}
          {location.type === "hotel" && location.hotel && (
            <div className="option-row">
              <div
                style={{
                  marginBottom: "0.5rem",
                  color: "white",
                  fontSize: "1rem",
                }}
              >
                ${location.hotel.price} {" restores "}
                {location.hotel.health} HP
              </div>
              <button
                className="menu-button"
                onClick={handleRentRoom}
                disabled={character.credits < location.hotel.price}
                style={{
                  opacity: character.credits < location.hotel.price ? 0.5 : 1,
                  cursor:
                    character.credits < location.hotel.price
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                Rent a {location.hotel.roomLabel}
              </button>
            </div>
          )}

          {/* Add Inventory and Help for top-level menus */}
          {isTopLevel && (
            <>
              <div className="option-row">
                <button
                  className="menu-button"
                  onClick={() => {
                    playClickSound();
                    onShowInventory();
                  }}
                >
                  Inventory
                </button>
              </div>
              <div className="option-row">
                <button
                  className="menu-button"
                  onClick={() => {
                    playClickSound();
                    onNavigate("help");
                  }}
                >
                  Help
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Hotel Rental Confirmation Dialog */}
      {showRentConfirmation && location.hotel && (
        <div className="confirmation-overlay">
          <div className="confirmation-dialog">
            <h3>Rent a {location.hotel.roomLabel}?</h3>
            <p>Cost: ${location.hotel.price}</p>
            <p>Health Restored: +{location.hotel.health} HP</p>
            <p>Current Credits: ${character.credits}</p>
            <div className="confirmation-buttons">
              <button className="confirm-button" onClick={confirmRentRoom}>
                Confirm
              </button>
              <button className="cancel-button" onClick={cancelRentRoom}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LocationMenu;
